import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { apiFetch } from "../../../lib/api";

const emptyProject = { title: "", slug: "", location: "", client_id: "", client_name: "", discipline: "", summary: "", stages: [], gallery: [], cover_image: "", status: "draft", featured: false, sort_order: 100 };

export const useProjectEditor = () => {
  const { projectId } = useParams();
  const editing = Boolean(projectId && projectId !== "new");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(emptyProject);
  const [stageInput, setStageInput] = useState("");
  const projectQuery = useQuery({ queryKey: ["admin-project", projectId], queryFn: () => apiFetch(`/admin/projects/${projectId}`), enabled: editing });
  const { data: clients = [] } = useQuery({ queryKey: ["admin-clients"], queryFn: () => apiFetch("/admin/clients") });
  const project = projectQuery.data;

  useEffect(() => { if (project) setForm(project); }, [project, setForm]);

  const refreshContent = (saved) => {
    queryClient.setQueryData(["admin-project", saved.project_id], saved);
    queryClient.invalidateQueries({ queryKey: ["admin-projects"] });
    queryClient.invalidateQueries({ queryKey: ["public-content"] });
    queryClient.invalidateQueries({ queryKey: ["public-project", saved.slug] });
  };

  const saveMutation = useMutation({
    mutationFn: () => apiFetch(editing ? `/admin/projects/${projectId}` : "/admin/projects", { method: editing ? "PUT" : "POST", body: JSON.stringify(form) }),
    onSuccess: (saved) => { refreshContent(saved); setForm(saved); toast.success("Project saved"); if (!editing) navigate(`/admin/projects/${saved.project_id}`, { replace: true }); },
    onError: (error) => toast.error(error.message),
  });
  const deleteMutation = useMutation({
    mutationFn: () => apiFetch(`/admin/projects/${projectId}`, { method: "DELETE" }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["admin-projects"] }); queryClient.invalidateQueries({ queryKey: ["public-content"] }); toast.success("Project deleted"); navigate("/admin/projects"); },
    onError: (error) => toast.error(error.message),
  });

  const update = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const selectClient = (clientId) => { const client = clients.find((item) => item.client_id === clientId); setForm((current) => ({ ...current, client_id: clientId, client_name: client?.name || "" })); };
  const addStage = () => { const stage = stageInput.trim(); if (!stage || form.stages.includes(stage)) return; update("stages", [...form.stages, stage]); setStageInput(""); };
  const removeStage = (index) => update("stages", form.stages.filter((_, itemIndex) => itemIndex !== index));
  const deleteProject = () => { if (window.confirm("Delete this project permanently?")) deleteMutation.mutate(); };

  return { projectId, editing, form, setForm, update, clients, selectClient, stageInput, setStageInput, addStage, removeStage, saveMutation, deleteMutation, deleteProject, projectQuery };
};